import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { SuccessBadge } from '@/components/SuccessBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { SCREEN, useKioskSession } from '@/lib/kioskSession';
import { useBack } from '@/shared/hooks/useBack';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import { usePayCardErrand } from '../hooks/usePayCardErrand';

/**
 * *"Your pay card is linked"* — the success state the registration never had
 * [KPC-1; `13 · Pay card linked` Desktop 12389:15932 · iPad 12389:16019 · Mobile 12389:16106, and
 * `R3 · Pay card linked` Desktop 12389:17070 · iPad 12389:17282 · Mobile 12389:17492 — all six resolved by
 * NAME on the kiosk page and read live 2026-09-14].
 *
 * A successful write used to exit straight to `finish` or back to the arrival menu, so the candidate did
 * the one thing that decides whether they get paid and was told nothing about it. The prototype names the
 * same defect in as many words: *"the flow had no success state: the candidate did the one thing that
 * determines whether they get paid and got no confirmation it worked."*
 *
 * ## Both routes reach it, and three sources agree — unlike `payCardWhy`
 *
 * - **Figma draws it twice, once per flow.** `13 · Pay card linked` sits in `09 · Get set up (pay card)`
 *   under `FLOW — NEW USER`; `R3 · Pay card linked` sits in `06a · Pay card (returning)`. Same copy, same
 *   structure, same measurements at all three widths — they differ only in the three things
 *   {@link usePayCardErrand} already decides.
 * - **The prototype, which governs flow [0155], has ONE screen** — `paycard-linked` — and every successful
 *   read reaches it: `if(scanState === 'ok'){ showStep('paycard-linked'); return; }`, with no branch on who
 *   the candidate is.
 * - **It is in the prototype's `HUB_TASK_SCREENS`**, the list documented as *"task screens the hub can send
 *   a returning candidate into. While in hub context their forward button returns to the hub instead of
 *   continuing the new-hire chain."* That is this screen's Continue, stated by the source that owns flow.
 *
 * `payCardWhy` is the contrast, not the precedent: it is journey-only because the hub wires
 * `returning-hub → paycard` DIRECTLY, past it. Nothing goes past this one.
 *
 * ## What the errand variant changes, and what it does not
 *
 * `FLOW_STEPS['paycard-linked']` puts it at `step: 3` of the apply flow — *Get set up*, the rail's fourth
 * of five, which is what both journey frames draw. The errand frames draw no rail: somebody replacing a
 * pay card is not being hired. Back is the mirror image — hidden at all three widths on the journey
 * (`Back button` instance `visible: false`), drawn at all three on the errand — and it is drawn where it
 * has somewhere to go, since a candidate on the journey reached this screen by finishing a registration
 * they cannot usefully return to.
 *
 * ## No help control, and therefore no queue entry
 *
 * [0220: *never promise a person without queueing one; never queue one without being asked*.] The frames
 * draw none at any width on either variant — the *Need help?* pill is the inert FAB every screen from
 * check-in onward carries. Arriving here promises nobody, so arriving here raises nobody, and
 * `summonRepresentative()` is deliberately not called: it is the single idempotent raiser and this screen
 * has no ask to raise it for.
 *
 * The `Action link →` under the callout is `visible: false` on all six frames and has no destination in
 * the file, so it is not built — the same placeholder `I9Screen` and `PayCardWhyScreen` both declined, for
 * the same reason: a link to nowhere on an unattended kiosk is worse than no link.
 *
 * ⛔ **None of the copy on this screen is confirmed, and the frame does not confirm it** — see the block
 * above the strings in `src/i18n/dictionary.ts`. Frame text is illustrative [0026, 0040]; the English is
 * transcribed so the screen is not blank and the Spanish is the app's own, both owed ISS's confirmation.
 */
export function PayCardLinkedScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const goTo = useKioskSession((state) => state.goTo);
  const isErrand = usePayCardErrand();
  /*
   * No `whenNotFromMenu`, because the control that calls this is rendered only on the errand — where
   * `arrival` is set by definition, so `useBack` takes its menu branch and the session-ending fallback is
   * unreachable. Passing one would be a destination nothing can arrive at.
   */
  const back = useBack();

  /*
   * ⚠️ **Exactly where the registration used to land, and that is the point of the whole change.** This
   * screen was inserted in front of two existing exits — `SCREEN.finish` on the application tail,
   * `SCREEN.arrivalReason` on the menu errand — so neither path may lose the one it had. The prototype
   * wires the same pair: its Continue targets `hiring-docs-final`, and the hub-context rule rewrites that
   * to `returning-hub` for a candidate who came in off the menu.
   */
  const whenDone = isErrand ? SCREEN.arrivalReason : SCREEN.finish;

  return (
    <KioskScreen>
      <KioskCard
        band={isErrand ? undefined : <Stepper current={JOURNEY_STEP.getSetUp} />}
        footer={
          /*
            ONE action, right-aligned from `sm` up and full width below it — the frames' own `Actions`
            band: HORIZONTAL with `primaryAxisAlignItems: MAX` around a 122x52 button at 1440 and 820,
            VERTICAL around a 343-wide one at 375. The width is the column's doing, not the button's, and
            one element in the DOM at every width — rendering a mobile button and a desktop button side by
            side would put two identically-named controls in the accessibility tree.
          */
          <div className="flex w-full flex-col sm:flex-row sm:items-center sm:justify-end">
            <Button
              onClick={() => {
                goTo(whenDone);
              }}
            >
              {t('payCardLinked.continue')}
            </Button>
          </div>
        }
        /*
          The journey frames set their `Back button` instance `visible: false` at 1440, 820 and 375 and
          leave the chip alone in the row; the returning frames draw it at all three. `KioskCard` keys its
          right-alignment of a lone chip on `data-badge` for exactly the first case.
        */
        header={
          <>
            {isErrand && <BackLink onClick={back} />}
            <StatusBadge label={t('badge.kiosk')} />
          </>
        }
        /*
          Measured per variant rather than matched to the screen next door, because the two frames really
          do differ at 375 and nowhere else.

          Journey — Desktop (12389:15932): card 92, Logo 92→203, Stepper 203→247, Header 247→295, Content
          295→631, Footer 631, every join flush; iPad (12389:16019) identical; Mobile (12389:16106) bar ends
          52 → Logo 76 → ends 147 → `Rail (mobile)` 171 → ends 219 → Content 243, three 24px gaps, because
          at 375 there is no card enclosing the slots. That is `banded` exactly, and the same shape
          `10 · Submitted` and `11 · Why a pay card` are drawn as.

          Errand — Desktop (12389:17070): card 92, Logo 92→203, Header 203→251, Content 251→819, Footer 819;
          Mobile (12389:17492) bar ends 52 → Logo 52 → ends 123 → Header 123 → ends 171 → Content 171, flush
          at every width including 375, which is `flush`. Its `Content` carries the 32px of top padding that
          the journey frame puts between the slots instead.
        */
        rhythm={isErrand ? 'flush' : 'banded'}
      >
        {/*
          `pb-8` is the frames' `Content` bottom padding at every width. The TOP is the one number that
          differs: `pad[T32]` on the errand frames at all three widths, and on the journey frames only from
          `sm` up — its 375 frame draws `pad[T0 R24 B32 L24]` and puts the 24px between the card's slots,
          which is `banded`'s job and not this column's.

          `items-center`: both frames' `Content` is `counterAxisAlignItems: CENTER` around a centred
          `Heading` block, which is what a confirmation is drawn as and what the form screens are not.
        */}
        <div
          className={`flex w-full flex-col items-center gap-6 pb-8 ${isErrand ? 'pt-8' : 'sm:pt-8'}`}
        >
          <SuccessBadge />

          {/* The frames group the title and its body as one block, 8px apart [`Heading`, 448x76]. */}
          <div className="flex w-full flex-col items-center gap-2">
            <h1
              className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-[var(--foreground)]"
              ref={heading}
              tabIndex={-1}
            >
              {t('payCardLinked.heading')}
            </h1>
            <p className="w-full text-center text-sm leading-5 text-[var(--muted-foreground)]">
              {t('payCardLinked.body')}
            </p>
          </div>

          {/*
            `Activate on your phone` — the brand-toned panel, #f3feff on #005d68 in the frame, and its text
            reads LEFT where everything above it is centred. It carries 0164: a card registered before the
            first payday cannot be loaded, so the candidate is told to leave it until then.
          */}
          <Callout>{t('payCardLinked.activate')}</Callout>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
